import i18n from 'common/util/internationalization'
import type { ContentWarning } from './writingStyleValidationTypes'
import { WARNING_TYPE_PROFANITY } from 'common/constants/constants'
import PROFANITY_WORDS from './profanityWords.json'

const profanityPattern = new RegExp(String.raw`\b(${PROFANITY_WORDS.join('|')})\b`, 'gi')

export function findProfanityHighlights(text: string): ContentWarning[] {
    const highlights: ContentWarning[] = []
    const re = new RegExp(profanityPattern.source, 'gi')
    let match: RegExpExecArray | null
    while ((match = re.exec(text)) !== null) {
        highlights.push({
            type: WARNING_TYPE_PROFANITY,
            match: match[0],
            offset: match.index,
            length: match[0].length
        })
    }
    return highlights
}


const DISALLOWED_CODE_CALL_PATTERNS: RegExp[] = [
    /\bprint\s*\(/i,
    /\bexec\s*\(/i,
    /\b__import__\s*\(/i,
    /\bsubprocess\.(?:run|call|Popen)\s*\(/i,
    /\bos\.(?:system|popen)\s*\(/i,
]

/**
 * Finds every occurrence of every disallowed pattern in `text` and returns
 * them as ContentWarning entries (type 'html_tag' → renders as red security
 * highlight in HighlightedTextarea). Uses global re-exec so multiple
 * occurrences of the same pattern are all captured.
 */
export function findSecurityHighlights(text: string): ContentWarning[] {
    const highlights: ContentWarning[] = []
    for (const pattern of DISALLOWED_CODE_CALL_PATTERNS) {
        const global = new RegExp(pattern.source, 'gi')
        let result: RegExpExecArray | null
        while ((result = global.exec(text)) !== null) {
            highlights.push({
                type: 'html_tag',
                match: result[0],
                offset: result.index,
                length: result[0].length,
            })
        }
    }
    return highlights.sort((a, b) => a.offset - b.offset)
}

export function findHTMLHighlights(text: string): ContentWarning[] {
    const highlights: ContentWarning[] = []
    const regex = /[<>]/g
    let match: RegExpExecArray | null
    while ((match = regex.exec(text)) !== null) {
        const idx = match.index
        const before = text.slice(0, idx).replace(/\s+$/, '')
        const after = text.slice(idx + 1).replace(/^\s+/, '')
        const isNumericComparison = /\d$/.test(before) && /^\d/.test(after)
        if (!isNumericComparison) {
            highlights.push({ type: 'html_tag', match: match[0], offset: idx, length: 1 })
        }
    }
    return highlights
}

type ValidResult = { ok: true }
type InvalidResult = {
    ok: false
    kind: 'empty' | 'security'
    message: string
    highlights: ContentWarning[]
}

/** Client-side pre-checks before PATCH; server always re-validates. */
export function validateStylePromptClient(text: string): ValidResult | InvalidResult {
    const trimmed = text.trim()
    if (!trimmed) {
        return { ok: false, kind: 'empty', message: i18n.t('STYLE_PROMPT_EMPTY_ERROR') as string, highlights: [] }
    }
    const highlights = [
        ...findSecurityHighlights(text),
        ...findHTMLHighlights(text),
    ].sort((a, b) => a.offset - b.offset)
    if (highlights.length > 0) {
        return {
            ok: false,
            kind: 'security',
            message: i18n.t('STYLE_PROMPT_HTML_CONTENT_ERROR') as string,
            highlights,
        }
    }
    return { ok: true }
}
